import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import {
  DocumentNode,
  LazyQueryExecFunction,
  OperationVariables,
  QueryHookOptions,
  QueryResult,
  useLazyQuery
} from '@apollo/client';
import { setError, setLoading } from '@common/slices';
import { extractMessage } from '@utils/helper-utils';

export function useApiLazyQuery<TData = any, TVariables = OperationVariables>(
  query: DocumentNode,
  key: string,
  options?: QueryHookOptions<TData, TVariables>,
  config?: { successMsg?: string; withLoader?: boolean }
): [LazyQueryExecFunction<TData, TVariables>, QueryResult<TData, TVariables>] {
  const dispatch = useDispatch();
  const withLoader = config?.withLoader ?? true; // default true
  const abortControllerRef = useRef<AbortController | null>(null);
  const optionsRef = useRef(options);
  const configRef = useRef(config);

  optionsRef.current = options;
  configRef.current = config;

  const context = useRef({
    fetchOptions: {
      signal: undefined as AbortSignal | undefined
    }
  }).current;

  // Set the signal if the request is active
  if (!context.fetchOptions.signal) {
    abortControllerRef.current = new AbortController();
    context.fetchOptions.signal = abortControllerRef.current.signal;
  }

  const [fetch, result] = useLazyQuery<TData, TVariables>(query, {
    ...options,
    context,
    onCompleted: (data: TData) => {
      if (withLoader) {
        dispatch(setLoading({ key, value: false }));
      }
      if (configRef.current?.successMsg) {
        toast.success(configRef.current.successMsg, { autoClose: 3000 });
      }
      if (optionsRef.current?.onCompleted) {
        optionsRef.current.onCompleted(data);
      }
    },
    onError: (error: any) => {
      if (withLoader) {
        dispatch(setLoading({ key, value: false }));
      }
      if (!error.message.includes('aborted')) {
        const cleanMessage = extractMessage(error.message);
        if (optionsRef.current?.onError) optionsRef.current.onError(error);
        dispatch(setError({ key, error: cleanMessage }));
        toast.error(cleanMessage, { autoClose: 3000 });
      }
    }
  });

  const wrappedFetch = useCallback(
    (...args: any) => {
      if (withLoader) {
        dispatch(setLoading({ key, value: true }));
      }
      dispatch(setError({ key, error: null }));

      return fetch(...args);
    },
    [dispatch, fetch, key, withLoader]
  ) as LazyQueryExecFunction<TData, TVariables>;

  // Cancel request on unmount only
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []); // Empty dependency array - only run on mount/unmount

  return [wrappedFetch, result];
}
